import React, { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, List, ListItem,
  ListItemText, TextField, Typography, makeStyles,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import api from '../../api/client'
import { toast } from '../../api/toast'
import JalaliDatePicker from '../JalaliDatePicker'
import { gregorianToJalali, jalaliToGregorian, toEnglishDigits, toFarsiDigits } from '../../assets/utils'

const useStyles = makeStyles(() => ({
  field: { marginBottom: 14, position: 'relative' },
  suggest: {
    position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%',
    background: '#fff', border: '1px solid #e0e4f0', borderRadius: 8,
    boxShadow: '0 10px 24px rgba(20,30,80,.12)', maxHeight: 220, overflowY: 'auto',
  },
  item: { cursor: 'pointer' },
  dateRow: { display: 'flex', gap: 8 },
}))

const todayJalali = () => {
  const j = toEnglishDigits(gregorianToJalali(new Date().toISOString().slice(0, 10)))
  const [y, m, d] = j.split('/')
  return { y, m, d }
}

export default function AddPremiumUserDialog({ open, onClose, onCreated }) {
  const classes = useStyles()
  const [q, setQ] = useState('')
  const [options, setOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [picked, setPicked] = useState(null)
  const [dateIso, setDateIso] = useState(() => {
    const t = new Date()
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
  })
  const [amount, setAmount] = useState('')
  const [fee, setFee] = useState('')
  const [busy, setBusy] = useState(false)

  // مبلغ پیش‌فرض از تنظیماتِ سایت (payment.subscription_fee) — نه عددِ ثابت
  useEffect(() => {
    api.get('users/site-settings/public/')
      .then((r) => {
        const f = r.data && r.data.payment && r.data.payment.subscription_fee
        if (f) {
          setFee(String(f))
          setAmount((a) => a || String(f))
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!open) return
    setQ(''); setOptions([]); setPicked(null)
    const t = new Date()
    setDateIso(`${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`)
    setAmount(fee || '')
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined
    if ((q || '').trim().length < 2) { setOptions([]); return undefined }
    let alive = true
    setSearching(true)
    const t = setTimeout(() => {
      api.get('users/admin/users/', { params: { page: 1, count: 15, search: q.trim() } })
        .then((r) => {
          const list = (r.data && (r.data.users || r.data.results)) || []
          if (alive) setOptions(list.filter((u) => (u.role || '').toLowerCase() === 'contractor'))
        })
        .catch(() => { if (alive) setOptions([]) })
        .finally(() => { if (alive) setSearching(false) })
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [q, open])

  const displayName = (u) => u.contractor_name || u.department_name || u.name || u.username

  const submit = async () => {
    if (!picked) return toast('ابتدا پیمانکار را انتخاب کنید', 'error')
    if (!dateIso) return toast('تاریخ پرداخت را انتخاب کنید', 'error')
    setBusy(true)
    try {
      await api.post('users/admin/add-subscription/', {
        user_id: picked.id,
        payment_date: dateIso,
        amount: Number(toEnglishDigits(amount)) || 0,
      })
      toast('کاربر ویژه ثبت شد', 'success')
      onClose?.()
      onCreated?.()
    } catch (e) {
      toast((e && e.response && e.response.data && e.response.data.detail) || 'خطا در ثبت کاربر ویژه', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        افزودن کاربر ویژه
        <IconButton style={{ position: 'absolute', left: 8, top: 8 }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box className={classes.field}>
          <TextField
            fullWidth
            label="جستجوی نام شرکت یا کد کاربری"
            variant="outlined"
            size="small"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null) }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {searching ? <CircularProgress size={18} /> : <SearchIcon />}
                </InputAdornment>
              ),
            }}
          />
          {options.length > 0 && (
            <List className={classes.suggest} dense>
              {options.map((u, i) => (
                <React.Fragment key={u.id}>
                  <ListItem className={classes.item} onClick={() => { setPicked(u); setQ(displayName(u)); setOptions([]) }}>
                    <ListItemText primary={displayName(u)} secondary={`کد: ${u.username}`} />
                  </ListItem>
                  {i < options.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
          {picked && (
            <Typography variant="caption" style={{ color: '#2e7d32', display: 'block', marginTop: 4 }}>
              ✓ انتخاب شد: {displayName(picked)} ({picked.username})
            </Typography>
          )}
        </Box>

        <Box className={classes.field}>
          <JalaliDatePicker
            label="تاریخ پرداخت (شمسی)"
            value={dateIso}
            onChange={setDateIso}
            helperText="اشتراک ۳۶۵ روز از این تاریخ محاسبه می‌شود."
          />
        </Box>

        <Box className={classes.field}>
          <TextField
            fullWidth
            label="مبلغ پرداخت (ریال)"
            variant="outlined"
            size="small"
            value={amount}
            onChange={(e) => setAmount(toEnglishDigits(e.target.value).replace(/\D/g, ''))}
            InputProps={{ endAdornment: <InputAdornment position="end">ریال</InputAdornment> }}
            helperText={amount ? `معادل ${toFarsiDigits(Math.round(Number(amount) / 10).toLocaleString('fa-IR'))} تومان` : 'فقط عدد'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={busy}>انصراف</Button>
        <Button onClick={submit} color="primary" variant="contained" disabled={busy}>
          {busy ? 'در حال ثبت...' : 'ثبت کاربر ویژه'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
